import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import { useConfig } from "../lib/config";
import { ROLE_LABEL } from "../lib/constants";
import LanguageSwitcher from "./LanguageSwitcher";
import {
  LogOut,
  LayoutDashboard,
  FileText,
  Activity,
  Upload,
  Users,
  Building2,
  School,
  Settings,
  Menu,
  X,
  Network,
} from "lucide-react";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { config } = useConfig();
  const location = useLocation();
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Auto-close mobile drawer when navigating
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock body scroll while drawer open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const navByRole = {
    admin: [
      { to: "/dashboard", label: t("nav.dashboard"), icon: LayoutDashboard },
      { to: "/convenios", label: t("nav.convenios"), icon: FileText },
      { to: "/convenios/new", label: t("nav.cargar_convenio"), icon: Upload },
      { to: "/acciones", label: t("nav.acciones"), icon: Activity },
      { to: "/admin/facultades", label: config.facultad_label_plural || t("nav.facultades"), icon: Building2 },
      ...(config.enable_unidades
        ? [{ to: "/admin/unidades", label: config.unidad_label_plural || t("nav.unidades"), icon: School }]
        : []),
      { to: "/admin/estructuras", label: t("nav.estructuras"), icon: Network },
      { to: "/admin/usuarios", label: t("nav.usuarios"), icon: Users },
      { to: "/admin/configuracion", label: t("nav.configuracion"), icon: Settings },
    ],
    facultad: [
      { to: "/dashboard", label: t("nav.mi_facultad"), icon: LayoutDashboard },
      { to: "/convenios", label: t("nav.convenios_mis"), icon: FileText },
      { to: "/acciones", label: t("nav.acciones"), icon: Activity },
      { to: "/acciones/new", label: t("nav.registrar_accion"), icon: Upload },
    ],
    unidad: [
      { to: "/dashboard", label: t("nav.mi_unidad"), icon: LayoutDashboard },
      { to: "/convenios", label: t("nav.convenios_mis"), icon: FileText },
      { to: "/acciones", label: t("nav.acciones"), icon: Activity },
      { to: "/acciones/new", label: t("nav.registrar_accion"), icon: Upload },
    ],
    directivo: [
      { to: "/dashboard", label: t("nav.dashboard"), icon: LayoutDashboard },
      { to: "/convenios", label: t("nav.convenios"), icon: FileText },
      { to: "/acciones", label: t("nav.acciones"), icon: Activity },
    ],
  };

  const nav = navByRole[user?.role] || [];

  const userSubtitle = user?.unidad_nombre
    ? `${ROLE_LABEL[user?.role]} · ${user.unidad_nombre}`
    : user?.facultad_nombre
    ? `${ROLE_LABEL[user?.role]} · ${user.facultad_nombre}`
    : ROLE_LABEL[user?.role];

  const SidebarContent = () => (
    <>
      <div className="px-6 py-6 lg:py-8 border-b border-zinc-200 flex items-center justify-between lg:block">
        <Link to="/dashboard" className="block" data-testid="brand-home">
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-semibold tracking-tighter text-zinc-950">vincularedu</span>
            <span className="text-2xl font-medium text-zinc-400">.</span>
          </div>
          <div className="section-marker mt-1 truncate" title={config.universidad_nombre}>
            {config.universidad_nombre || "Inteligencia de vinculación"}
          </div>
        </Link>
        <button
          className="lg:hidden text-zinc-500 hover:text-zinc-950 p-2 -mr-2"
          onClick={() => setMobileOpen(false)}
          aria-label="Cerrar menú"
          data-testid="sidebar-close"
        >
          <X size={22} strokeWidth={1.5} />
        </button>
      </div>
      <nav className="flex-1 py-4 lg:py-6 overflow-y-auto">
        {nav.map((item) => {
          const Icon = item.icon;
          const active =
            location.pathname === item.to ||
            (item.to !== "/dashboard" && location.pathname.startsWith(item.to));
          return (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.replace(/\//g, "-")}`}
              className={`flex items-center gap-3 px-6 py-3 text-sm border-l-2 transition-colors ${
                active
                  ? "border-zinc-950 bg-zinc-50 text-zinc-950 font-medium"
                  : "border-transparent text-zinc-500 hover:text-zinc-950 hover:bg-zinc-50"
              }`}
            >
              <Icon size={18} strokeWidth={1.5} />
              <span className="truncate">{item.label}</span>
            </Link>
          );
        })}
      </nav>
      <div className="border-t border-zinc-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-full bg-zinc-950 text-white flex items-center justify-center text-sm font-medium shrink-0">
            {(user?.nombre || "U").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium truncate" data-testid="user-name">{user?.nombre}</div>
            <div className="section-marker truncate" data-testid="user-role" title={userSubtitle}>{userSubtitle}</div>
          </div>
        </div>
        <button
          onClick={logout}
          className="w-full flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-950 py-2"
          data-testid="logout-btn"
        >
          <LogOut size={16} strokeWidth={1.5} />
          {t("nav.cerrar_sesion")}
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-white text-zinc-950 lg:flex">
      {/* Mobile top bar */}
      <header
        className="lg:hidden sticky top-0 z-30 bg-white border-b border-zinc-200 flex items-center justify-between px-4 h-14"
        data-testid="mobile-topbar"
      >
        <Link to="/dashboard" className="flex items-baseline gap-1">
          <span className="text-xl font-semibold tracking-tighter text-zinc-950">vincularedu</span>
          <span className="text-xl font-medium text-zinc-400">.</span>
        </Link>
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <button
            onClick={() => setMobileOpen(true)}
            className="text-zinc-700 hover:text-zinc-950 p-2 -mr-2"
            aria-label="Open menu"
            data-testid="mobile-menu-btn"
          >
            <Menu size={22} strokeWidth={1.5} />
          </button>
        </div>
      </header>

      {/* Mobile drawer overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setMobileOpen(false)}
          aria-hidden
        />
      )}

      {/* Mobile drawer */}
      <aside
        className={`lg:hidden fixed top-0 left-0 bottom-0 w-[85%] max-w-[320px] bg-white border-r border-zinc-200 z-50 flex flex-col transition-transform duration-200 ease-out ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="sidebar-mobile"
      >
        <SidebarContent />
      </aside>

      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex w-64 shrink-0 border-r border-zinc-200 min-h-screen flex-col"
        data-testid="sidebar"
      >
        <SidebarContent />
      </aside>

      <main className="flex-1 min-w-0">
        {/* Desktop top bar — siempre visible */}
        <div
          className="hidden lg:flex sticky top-0 z-20 bg-white/95 backdrop-blur-sm border-b border-zinc-200 px-12 h-14 items-center justify-end gap-6"
          data-testid="desktop-topbar"
        >
          <LanguageSwitcher />
        </div>
        {children}
      </main>
    </div>
  );
}
